import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

/* One gesture has to land exactly one card. Swiper's short swipe branch adds a
   whole slide on top of whichever slide the finger had already dragged the track
   into, so a flick that carries the track a full slide step and finishes inside
   longSwipesMs arrives two cards on. A step here is 280px, a card plus its 16px
   gap, and a 280px flick is an ordinary thumb on a 390px phone. Swiper emits
   touchEnd before it works out where to snap, so capping the release position to
   just under one step leaves its own arithmetic to add the single card. Anything
   shorter than a step is left alone and feels the same.
   @param {any} sw */
function prCapSwipeToOneSlide(sw) {
  if (!sw || !sw.touchEventsData || !sw.slidesSizesGrid) return;

  /* Free mode is on from 750px up and hands the release off to its own momentum
     path before the snap arithmetic runs, so there is nothing there to cap. */
  if (sw.params.freeMode && sw.params.freeMode.enabled) return;

  const start = sw.touchEventsData.startTranslate;
  if (typeof start !== 'number') return;

  const slideWidth = sw.slidesSizesGrid[sw.activeIndex];
  const gap = parseFloat(sw.params.spaceBetween) || 0;
  const step = slideWidth ? slideWidth + gap : 0;
  if (!step) return;

  /* Just under a whole step. Landing exactly on the boundary would read as the
     next card and the increment would stack on top of that instead. */
  const cap = step * 0.9;
  const moved = sw.translate - start;
  if (Math.abs(moved) <= cap) return;

  const capped = start + (moved < 0 ? -cap : cap);
  sw.translate = capped;
  sw.touchEventsData.currentTranslate = capped;
}

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });

          /* Product_recommendations_slider */
          document.querySelectorAll('.Product_recommendations_slider').forEach(el => {
            if (!el.classList.contains('swiper-initialized')) {
              new Swiper(el, {
                loop: false,
                grabCursor: true,
                spaceBetween: 16,
                slidesPerGroup: 1,
                slidesPerView: "auto",
                watchSlidesProgress: true,
                draggable: !0,
                autoHeight: !1,
                watchOverflow: !0,
                threshold: 10,
                mousewheel: {
                    forceToAxis: !0
                },
                observer: true,
                observeParents: true,
                freeMode: !0,
                breakpoints: {
                    0: {
                        freeMode: !1
                    },
                    750: {
                    },
                    990: {
                    },
                    1200: {
                    },
                    1440: {
                    }
                },
                on: {
                  touchEnd: function () {
                    prCapSwipeToOneSlide(this);
                  }
                }
              })
            }
          });
          /* Product_recommendations_slider */
          
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}

